import { AppointmentPhase } from "components";
import { DateTimePicker } from "components/DatePicker/DateTimePicker";
import AirlinePaginatedDropdown from "components/dropdowns/paginated/AirlinePaginatedDropdown";
import AirportPaginatedDropdown from "components/dropdowns/paginated/AirportPaginatedDropdown";
import { AppointmentPhaseDropdown } from "components/dropdowns/reactselect/AppointmentPhaseDropdown";
import { RenderEditOptions, Section } from "components/MainSectionTemplate/types";
import ArtistModalPicker from "components/Picker/ArtistPicker/ArtistModalPicker";
import { CompanySidePanel } from "components/SidePanel/Company/CompanySidePanel";
import SmallDetails from "components/SmallDetails/SmallDetails";
import OptionalNumberInput from "components/ValidatedFields/OptionalNumberInput";
import OptionalTextAreaInput, { OptionalTextAreaDetails } from "components/ValidatedFields/OptionalTextAreaInput";
import OptionalTextInput from "components/ValidatedFields/OptionalTextInput";
import moment from "moment";
import React from "react";
import { getAirportCode } from "shared/utils/flightUtils";
import { formatTime, formatWrittenDayMonthYear } from "utilities";
import { FlightDesignatorInput } from "./FlightDesignatorInput";
import { FormatArrivalTime } from "./lookup/FlightSearchResult";
import { FlightFieldMetaData, FlightFormState } from "shared/utils/fieldsHelper/flightFields";

export const flightSections = (
  state: FlightFormState,
  fieldMetaData: FlightFieldMetaData,
  getUpdateFlightInstruction: (f: FlightFormState) => server.dto.UpdateFlightInstruction,
): Array<Section> => [{
  key: "flight",
  type: "regular",
  title: state.flightType === "private" ? RESX.Flight.PrivateFlight : RESX.Flight.resxFlight,
  fields: [{
    ...fieldMetaData.artist.fields(),
    key: "artist",
    label: site.currentEnvironment.functionGroups.artist.singular,
    render: () => <CompanySidePanel type="artist"
      companyId={state.artist.artistId}
      trigger={<a>{state.artist.name}</a>}
      fallbackIfNoPermission={<>{state.artist.name}</>}
    />,
    inPageEdit: {
      hoverEdit: true,
      render: ({ onChange, cancel, value }) => <div className="main-section-template-fields-padding">
        <ArtistModalPicker
          inPageEditing={{ onChange, cancel, hasValue: Boolean(value) }}
          securityScope="limitByUserAccess"
          canCreateNew={false}
        />
      </div>,
      fullWidth: false,
      type: "custom",
      initialValue: state.artist,
      submitChange: async (artist: server.dto.ArtistSmall) => window.CoreApi.Flight.UpdateFlight(
        getUpdateFlightInstruction({ ...state, artist }),
      ),
    },
  }, {
    ...fieldMetaData.phase.fields(),
    key: "status",
    label: RESX.Status.resxStatus,
    render: () => <AppointmentPhase value={state.phase.value} displayLabel />,
    inPageEdit: {
      render: ({ value, onChange, cancel }: RenderEditOptions<server.dto.AppointmentPhase>) => <AppointmentPhaseDropdown
        value={value}
        onChange={(appointmentPhase) => {
          onChange(appointmentPhase, true);
        }}
        onEsc={() => cancel()}
        config={{
          focusOnMount: true,
          openMenuOnFocus: true,
        }}
      />,
      fullWidth: true,
      type: "custom",
      initialValue: state.phase,
      submitChange: (phase: server.dto.AppointmentPhase) => window.CoreApi.Flight.UpdateFlight({
        ...getUpdateFlightInstruction({ ...state, phase }),
      }),
    },
  }, {
    ...fieldMetaData.travelAgency.fields(),
    key: "travelAgency",
    label: RESX.Flight.TravelAgency,
    render: () => <div>{state.travelAgency}</div>,
    inPageEdit: {
      render: ({ value, onChange, submit, cancel }: RenderEditOptions<string>) => <OptionalTextInput
        focusOnMount
        maxLength={serverReference.constants.flight.maxTravelAgencyLength}
        value={value}
        onChange={(v) => { onChange(v); }}
        onEnter={() => submit()}
        onEsc={() => cancel()}
      />,
      fullWidth: true,
      type: "confirmOrCancelButton",
      initialValue: state.travelAgency ?? "",
      submitChange: (travelAgency: string) => window.CoreApi.Flight.UpdateFlight({
        ...getUpdateFlightInstruction({ ...state, travelAgency }),
      }),
    }
  }, {
    ...fieldMetaData.notes.fields(),
    key: "notes",
    label: RESX.Note.Notes,
    render: () => <OptionalTextAreaDetails>{state.notes}</OptionalTextAreaDetails>,
    inPageEdit: {
      render: ({ value, onChange, submit, cancel }: RenderEditOptions<string>) => <OptionalTextAreaInput
        focusOnMount
        maxLength={serverReference.constants.flight.maxNotesLength}
        rows={3}
        value={value}
        onChange={(v) => { onChange(v); }}
        onCtrlEnter={() => submit()}
        onEsc={() => cancel()}
      />,
      fullWidth: true,
      type: "confirmOrCancelButton",
      initialValue: state.notes ?? "",
      submitChange: (notes: string) => window.CoreApi.Flight.UpdateFlight({
        ...getUpdateFlightInstruction({ ...state, notes }),
      }),
    },
  }, {
    ...fieldMetaData.airline.fields(),
    key: "airline",
    label: RESX.Flight.Airline,
    render: () => <SmallDetails
      label={state.airline.name}
    />,
    inPageEdit: {
      render: ({ value, onChange, cancel }: RenderEditOptions<server.dto.Airline>) => <AirlinePaginatedDropdown
        displayFriendyCodeInSelectedValue={false}
        value={value}
        onChange={(airline) => {
          onChange(airline, true);
        }}
        onEsc={() => cancel()}
        config={{
          focusOnMount: true,
          isSearchable: true,
          openMenuOnFocus: true,
        }}
      />,
      fullWidth: true,
      type: "custom",
      initialValue: state.airline,
      submitChange: (airline: server.dto.Airline) => window.CoreApi.Flight.UpdateFlight(
        getUpdateFlightInstruction({ ...state, airline }),
      ),
    }
  }, {
    ...fieldMetaData.flightDesignator.fields(),
    key: "flightDesignator",
    label: RESX.Flight.FlightNumber,
    render: () => <div>{state.flightDesignator}</div>,
    inPageEdit: {
      render: ({ value, onChange, cancel, submit }: RenderEditOptions<string>) => <FlightDesignatorInput
        value={value}
        placeholderAirline={state.airline?.iata || state.airline?.icao || ""}
        validatorKey="flightCode"
        onChange={(fd) => onChange(fd)}
        focusOnMount
        onEsc={() => cancel()}
        onEnter={() => submit()}
      />,
      initialValue: state.flightDesignator,
      fullWidth: false,
      type: "confirmOrCancelButton",
      submitChange: (flightDesignator: string) => window.CoreApi.Flight.UpdateFlight({
        ...getUpdateFlightInstruction({ ...state, flightDesignator }),
      }),
    }
  }, {
    ...fieldMetaData.access.fields(),
    key: "access",
    label: RESX.SoUser.Access,
    render: () => <OptionalTextAreaDetails>{state.access}</OptionalTextAreaDetails>,
    inPageEdit: {
      render: ({ value, onChange, submit, cancel }: RenderEditOptions<string>) => <OptionalTextAreaInput
        focusOnMount
        maxLength={serverReference.constants.flight.maxPrivateJetAccessLength}
        value={value}
        onChange={(v) => { onChange(v); }}
        onCtrlEnter={() => submit()}
        onEsc={() => cancel()}
      />,
      fullWidth: true,
      type: "confirmOrCancelButton",
      initialValue: state.access ?? "",
      submitChange: (access: string) => window.CoreApi.Flight.UpdateFlight({
        ...getUpdateFlightInstruction({ ...state, access }),
      }),
    }
  }, {
    ...fieldMetaData.confirmationNumber.fields(),
    key: "confirmationNumber",
    label: RESX.Flight.ConfirmationNumber,
    render: () => <div>{state.confirmationNumber}</div>,
    inPageEdit: {
      render: ({ value, onChange, submit, cancel }: RenderEditOptions<string>) => <OptionalTextInput
        focusOnMount
        maxLength={serverReference.constants.flight.maxPrivateJetConfirmationNumberLength}
        value={value}
        onChange={(v) => { onChange(v); }}
        onEnter={() => submit()}
        onEsc={() => cancel()}
      />,
      fullWidth: true,
      type: "confirmOrCancelButton",
      initialValue: state.confirmationNumber ?? "",
      submitChange: (confirmationNumber: string) => window.CoreApi.Flight.UpdateFlight({
        ...getUpdateFlightInstruction({ ...state, confirmationNumber }),
      }),
    }
  }, {
    ...fieldMetaData.capacity.fields(),
    key: "capacity",
    label: RESX.Venue.Capacity,
    render: () => <div>{state.capacity} PAX</div>,
    inPageEdit: {
      render: ({ value, onChange, cancel }: RenderEditOptions<number>) => <OptionalNumberInput
        autoFocus
        width={100}
        decimalPoints={0}
        maxValue={1000}
        value={value}
        rightLabel="PAX"
        onChange={(v) => { onChange(v); }}
        onEnterPressed={(v) => onChange(v, true)}
        onEscPressed={() => cancel()}
      />,
      fullWidth: false,
      type: "confirmOrCancelButton",
      initialValue: state.capacity ?? 0,
      submitChange: (capacity: number) => window.CoreApi.Flight.UpdateFlight({
        ...getUpdateFlightInstruction({ ...state, capacity }),
      }),
    }
  }]
}, {
  key: "departureArrival",
  title: "",
  hideTitle: true,
  type: "regular",
  fields: [{
    key: "departureArrivalLabel",
    label: "",
    left: {
      key: "departureLabel",
      render: () => <h3>{RESX.Flight.Departure}</h3>
    },
    right: {
      key: "arrivalLabel",
      render: () => <h3>{RESX.Flight.Arrival}</h3>
    }
  }, {
    key: "date",
    label: RESX.DateTime.lblDate,
    left: {
      key: "departureLocal",
      render: () => <div className="flex-column">
        <div>{formatTime(state.departureLocal)}</div>
        <div>{formatWrittenDayMonthYear(state.departureLocal)}</div>
      </div>,
      inPageEdit: {
        render: ({ value, onChange, submit, cancel }: RenderEditOptions<moment.Moment>) => <DateTimePicker
          value={value}
          onChange={v => onChange(v)}
          validatorKey="departureDate"
          minDate={moment(serverReference.constants.appointment.minDate)}
          maxDate={moment(serverReference.constants.appointment.maxDate)}
          onEnter={submit}
          onEsc={cancel}
        />,
        type: "confirmOrCancelButton",
        fullWidth: true,
        initialValue: state.departureLocal,
        submitChange: (departureLocal: moment.Moment) => {
          const diff = departureLocal.diff(state.departureLocal, "minutes");
          const arrivalLocal = state.arrivalLocal.clone().add(diff, "minutes");
          return window.CoreApi.Flight.UpdateFlight({
            ...getUpdateFlightInstruction({ ...state, departureLocal, arrivalLocal }),
          })
        }
      }
    },
    right: {
      key: "arrivalLocal",
      render: () => <FormatArrivalTime departure={state.departureLocal} arrival={state.arrivalLocal} />,
      inPageEdit: {
        render: ({ value, onChange, submit, cancel }: RenderEditOptions<moment.Moment>) => <DateTimePicker
          value={value}
          onChange={v => onChange(v)}
          validatorKey="arrivalDate"
          minDate={state.departureLocal.clone().add(serverReference.constants.flight.maxArrivalBeforeDepartureHours, "hours")}
          maxDate={state.departureLocal.clone().add(serverReference.constants.flight.maxArrivalAfterDepartureHours, "hours")}
          onEnter={submit}
          onEsc={cancel}
        />,
        type: "confirmOrCancelButton",
        fullWidth: true,
        initialValue: state.arrivalLocal,
        submitChange: (arrivalLocal: moment.Moment) => window.CoreApi.Flight.UpdateFlight({
          ...getUpdateFlightInstruction({ ...state, arrivalLocal }),
        }),
      }
    }
  }, {
    key: "airport",
    label: RESX.Flight.Airport,
    left: {
      key: "departureAirport",
      render: () => <SmallDetails
        label={`${state.departureAirport.name} (${getAirportCode(state.departureAirport)})`}
        subLabel={state.departureAirport.location}
      />,
      inPageEdit: {
        render: ({ value, onChange, cancel }: RenderEditOptions<server.dto.Airport>) => <AirportPaginatedDropdown
          value={value}
          onChange={(airport) => onChange(airport, true)}
          onEsc={() => cancel()}
          config={{
            focusOnMount: true,
            isSearchable: true,
            openMenuOnFocus: true,
          }}
        />,
        type: "custom",
        initialValue: state.departureAirport,
        fullWidth: true,
        submitChange: (departureAirport: server.dto.Airport) => window.CoreApi.Flight.UpdateFlight({
          ...getUpdateFlightInstruction({ ...state, departureAirport }),
        }),
      }
    },
    right: {
      key: "arrivalAirport",
      render: () => <SmallDetails
        label={`${state.arrivalAirport.name} (${getAirportCode(state.arrivalAirport)})`}
        subLabel={state.arrivalAirport.location}
      />,
      inPageEdit: {
        render: ({ value, onChange, cancel }: RenderEditOptions<server.dto.Airport>) => <AirportPaginatedDropdown
          value={value}
          onChange={(airport) => onChange(airport, true)}
          onEsc={() => cancel()}
          config={{
            focusOnMount: true,
            isSearchable: true,
            openMenuOnFocus: true,
          }}
        />,
        type: "custom",
        initialValue: state.arrivalAirport,
        fullWidth: true,
        submitChange: (arrivalAirport: server.dto.Airport) => window.CoreApi.Flight.UpdateFlight({
          ...getUpdateFlightInstruction({ ...state, arrivalAirport }),
        }),
      }
    }
  }, {
    key: "terminal",
    label: RESX.Flight.Terminal,
    left: {
      ...fieldMetaData.departureTerminal.fields(),
      key: "departureTerminal",
      render: () => <div>{state.departureTerminal}</div>,
      inPageEdit: {
        render: ({ value, onChange, submit, cancel }: RenderEditOptions<string>) => <OptionalTextInput
          focusOnMount
          width={60}
          maxLength={serverReference.constants.flight.maxTerminalLength}
          value={value}
          onChange={(v) => { onChange(v); }}
          onEnter={() => submit()}
          onEsc={() => cancel()}
        />,
        fullWidth: false,
        type: "confirmOrCancelButton",
        initialValue: state.departureTerminal ?? "",
        submitChange: (departureTerminal: string) => window.CoreApi.Flight.UpdateFlight({
          ...getUpdateFlightInstruction({ ...state, departureTerminal }),
        }),
      }
    },
    right: {
      ...fieldMetaData.arrivalTerminal.fields(),
      key: "arrivalTerminal",
      render: () => <div>{state.arrivalTerminal}</div>,
      inPageEdit: {
        render: ({ value, onChange, submit, cancel }: RenderEditOptions<string>) => <OptionalTextInput
          focusOnMount
          width={60}
          maxLength={serverReference.constants.flight.maxTerminalLength}
          value={value}
          onChange={(v) => { onChange(v); }}
          onEnter={() => submit()}
          onEsc={() => cancel()}
        />,
        fullWidth: false,
        type: "confirmOrCancelButton",
        initialValue: state.arrivalTerminal ?? "",
        submitChange: (arrivalTerminal: string) => window.CoreApi.Flight.UpdateFlight({
          ...getUpdateFlightInstruction({ ...state, arrivalTerminal }),
        }),
      }
    }
  },

    // , {
    //   key: "distanceAndDuration",
    //   label: "",
    //   hideLabel: true,
    //   hidden: !state.distanceInMeters || !state.durationInMinutes,
    //   render: () => <div className="radius card-floating-bg padding-top-large padding-bottom-large padding-left padding-right">
    //     <div>{RESX.Flight.Distance}:&nbsp;&nbsp;{formatDistance(state.distanceInMeters)}</div>
    //     {state.durationInMinutes && <div>{RESX.Flight.Duration}:&nbsp;&nbsp;{formatTimeDifference(moment.utc(), moment.utc().add(state.durationInMinutes, "minute"))}</div>}
    //   </div>
    // }
  ]
}];